import * as moment from 'moment';
import { useMemo } from 'react';
import { Utility } from '@nkr/services/utility';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { IDonorsProtectedReport } from '@nkr/store/reports/donors-protected';

interface IProps {
    data: IDonorsProtectedReport[]
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const cols = [
        {
            headerName: "Kidney Donors",
            headerClass: 'list-header',
            children: [
                {
                    colId: 'monthEnd',
                    field: 'monthEnd',
                    headerName: 'Month End',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Month End</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                },
                {
                    colId: 'kidneyDonorsFullyProtected',
                    field: 'kidneyDonorsFullyProtected',
                    headerName: 'Kidney Donors Fully Protected',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Fully Protected</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                },
                {
                    colId: 'kidneyDonorsProtected',
                    field: 'kidneyDonorsProtected',
                    headerName: 'Kidney Donors Protected',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Protected</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                },
                {
                    colId: 'kidneyDonorsTotal',
                    field: 'kidneyDonorsTotal',
                    headerName: 'Reported Total Donations',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>of Total</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                },
                {
                    colId: 'percentKidneyDonorsProtected',
                    headerName: '% of Total',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>% Protected</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                    valueGetter: (params: ValueGetterParams) => Math.round((params.data.kidneyDonorsProtected / params.data.kidneyDonorsTotal) * 100),
                    valueFormatter: (params: ValueGetterParams) => {
                        const percent = Math.round((params.data.kidneyDonorsProtected / params.data.kidneyDonorsTotal) * 100);
                        return percent > 0 ? `${percent}%` : '0%';
                    }
                },
            ]
        },
        {
            headerName: "Liver Donors",
            headerClass: 'list-header',
            children: [
                {
                    colId: 'liverDonorsProtected',
                    field: 'liverDonorsProtected',
                    headerName: 'Protected',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Protected</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                },
                {
                    colId: 'liverDonorsTotal',
                    field: 'liverDonorsTotal',
                    headerName: 'of Total',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>of Total</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                },
                {
                    colId: 'percentKidneyDonorsProtected',
                    headerName: '% of Total',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>% Protected</SortableHeaderComponent>,
                    width: 125,
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                    valueGetter: (params: ValueGetterParams) => Math.round((params.data.liverDonorsProtected / params.data.liverDonorsTotal) * 100),
                    valueFormatter: (params: ValueGetterParams) => {
                        const percent = Math.round((params.data.liverDonorsProtected / params.data.liverDonorsTotal) * 100);
                        return percent > 0 ? `${percent}%` : '0%';
                    }
                },
            ]
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={30}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No data found for date range...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onRowDataChanged={onRowDataChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;